import React, { useEffect, useState } from 'react';
import type { CategoryRecord, TaskRecord, TaskStatus, TaskUpdatePayload } from '../api';
import { describeDate, fromDateTimeInputValue, isOverdue, isToday, toDateTimeInputValue } from '../utils/date';

const PRIORITY_OPTIONS = [
  { value: 1, label: 'Urgent', color: 'var(--urgent)' },
  { value: 2, label: 'High', color: 'var(--high)' },
  { value: 3, label: 'Normal', color: 'var(--normal)' },
  { value: 4, label: 'Low', color: 'var(--low)' },
] as const;

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
  { value: 'blocked', label: 'Blocked' },
];

interface Props {
  task: TaskRecord;
  categories: CategoryRecord[];
  onUpdate: (id: string, updates: TaskUpdatePayload) => Promise<void> | void;
  onDelete: (id: string) => Promise<void> | void;
  onClose: () => void;
}

export function TaskDetail({ task, categories, onUpdate, onDelete, onClose }: Props) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [notes, setNotes] = useState(task.notes || '');
  const [progress, setProgress] = useState(task.percentDone || 0);

  useEffect(() => {
    setTitle(task.title);
    setNotes(task.notes || '');
    setProgress(task.percentDone || 0);
  }, [task.id, task.notes, task.percentDone, task.title]);
  const handleSaveProgress = () => {
    if (progress !== (task.percentDone || 0)) {
      onUpdate(task.id, { percent_done: progress });
    }
  };


  const handleSaveTitle = () => {
    if (title.trim() && title !== task.title) {
      onUpdate(task.id, { title: title.trim() });
    }
    setEditing(false);
  };

  const handleSaveNotes = () => {
    if (notes !== (task.notes || '')) {
      onUpdate(task.id, { notes });
    }
  };

  return (
    <div className="detail-panel">
      <button className="close-btn" onClick={onClose}>✕</button>

      <div style={{ marginBottom: 20, marginTop: 8 }}>
        {editing ? (
          <input
            className="form-input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onBlur={handleSaveTitle}
            onKeyDown={(event) => event.key === 'Enter' && handleSaveTitle()}
            autoFocus
            style={{ fontSize: 18, fontWeight: 700 }}
          />
        ) : (
          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              cursor: 'pointer',
              textDecoration: task.status === 'done' ? 'line-through' : 'none',
              opacity: task.status === 'done' ? 0.6 : 1,
            }}
            onClick={() => setEditing(true)}
          >
            {task.title}
          </h2>
        )}
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          <StatusChip active={isToday(task.dueDate)} label="Due today" />
          <StatusChip active={isOverdue(task.dueDate)} label="Overdue" tone="danger" />
          <StatusChip active={Boolean(task.scheduledStart)} label="Scheduled" tone="accent" />
        </div>
      </div>

      <div className="form-group">
        <label>Status</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              className={`chip ${task.status === option.value ? 'active' : ''}`}
              onClick={() => onUpdate(task.id, { status: option.value })}
              style={{ fontSize: 12 }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>Priority</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {PRIORITY_OPTIONS.map((option) => (
            <button
              key={option.value}
              className={`chip ${task.priority === option.value ? 'active' : ''}`}
              onClick={() => onUpdate(task.id, { priority: option.value })}
              style={task.priority === option.value ? { borderColor: option.color, color: option.color } : { fontSize: 12 }}
            >
              <span
                className={`priority-dot priority-${option.value}`}
                style={{ width: 6, height: 6, borderRadius: '50%', display: 'inline-block' }}
              />
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>Category</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <button
            className={`chip ${!task.categoryId ? 'active' : ''}`}
            onClick={() => onUpdate(task.id, { category_id: null })}
          >
            None
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              className={`chip ${task.categoryId === category.id ? 'active' : ''}`}
              onClick={() => onUpdate(task.id, { category_id: category.id })}
            >
              {category.icon} {category.name}
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>Due date</label>
        <input
          type="date"
          className="form-input"
          value={task.dueDate || ''}
          onChange={(event) => onUpdate(task.id, { due_date: event.target.value || null })}
          style={{ colorScheme: 'dark' }}
        />
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>{describeDate(task.dueDate)}</p>
      </div>

      <div className="form-group">
        <label>Due time</label>
        <input
          type="time"
          className="form-input"
          value={task.dueTime || ''}
          onChange={(event) => onUpdate(task.id, { due_time: event.target.value || null })}
          style={{ colorScheme: 'dark' }}
        />
      </div>

      <div className="form-group">
        <label>Scheduled start</label>
        <input
          type="datetime-local"
          className="form-input"
          value={toDateTimeInputValue(task.scheduledStart)}
          onChange={(event) => onUpdate(task.id, { scheduled_start: fromDateTimeInputValue(event.target.value) })}
          style={{ colorScheme: 'dark' }}
        />
      </div>

      <div className="form-group">
        <label>Scheduled end</label>
        <input
          type="datetime-local"
          className="form-input"
          value={toDateTimeInputValue(task.scheduledEnd)}
          onChange={(event) => onUpdate(task.id, { scheduled_end: fromDateTimeInputValue(event.target.value) })}
          style={{ colorScheme: 'dark' }}
        />
        {(task.scheduledStart || task.scheduledEnd) && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginTop: 8 }}
            onClick={() => onUpdate(task.id, { scheduled_start: null, scheduled_end: null })}
          >
            Remove schedule
          </button>
        )}
      </div>

      <div className="form-group">
        <label>Progress</label>
        <input
          type="number"
          min="0"
          max="100"
          step="5"
          className="form-input"
          value={progress}
          onChange={(event) => setProgress(Number(event.target.value))}
          onBlur={handleSaveProgress}
        />
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>{progress}% complete</div>
      </div>

      <div className="form-group">
        <label>Notes</label>
        <textarea
          className="form-input"
          rows={6}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          onBlur={handleSaveNotes}
          placeholder="Add notes, context, next steps, or a quick work log..."
          style={{ resize: 'vertical', fontFamily: 'var(--font)' }}
        />
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 20 }}>
        <div>Created: {task.createdAt ? new Date(task.createdAt).toLocaleString() : '—'}</div>
        <div>Updated: {task.updatedAt ? new Date(task.updatedAt).toLocaleString() : '—'}</div>
        {task.completedAt && <div>Completed: {new Date(task.completedAt).toLocaleString()}</div>}
      </div>

      <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
        <button
          className="btn btn-ghost"
          style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
          onClick={() => {
            if (confirm('Delete this task?')) {
              onDelete(task.id);
            }
          }}
        >
          Delete task
        </button>
      </div>
    </div>
  );
}

function StatusChip({
  active,
  label,
  tone,
}: {
  active: boolean;
  label: string;
  tone?: 'accent' | 'danger';
}) {
  const borderColor = tone === 'danger' ? 'var(--danger)' : 'var(--accent)';
  const color = tone === 'danger' ? 'var(--danger)' : 'var(--accent)';

  return (
    <span
      className={`chip ${active ? 'active' : ''}`}
      style={active ? { borderColor, color } : undefined}
    >
      {label}
    </span>
  );
}
